<script lang="ts">
	import ChevronDown from '@lucide/svelte/icons/chevron-down'
	import ChevronUp from '@lucide/svelte/icons/chevron-up'
	import CircleHelp from '@lucide/svelte/icons/circle-help'
	import ToggleLeft from '@lucide/svelte/icons/toggle-left'
	import ToggleRight from '@lucide/svelte/icons/toggle-right'
	import { Button as ButtonPrimitive } from 'bits-ui'
	import { cn } from 'tailwind-variants'

	import InfoPopover from '#lib/components/info-popover.svelte'
	import * as Card from '#lib/components/ui/card/index.js'
	import * as Tooltip from '#lib/components/ui/tooltip/index.js'
	import type { ChainData } from '#lib/models/index.js'
	import { updateMetricDialog, type MetricDialogProps } from '#lib/utils/dialogs.svelte.js'
	import { formatChange } from '#lib/utils/formatters.js'
	import { n } from '#lib/utils/navigation.svelte.js'
	import { phg } from '#lib/utils/posthog.js'

	let {
		chain,
		name,
		showIcon,
		actionState,
		actionText,
		popoverContent: content,
		change,
		inverseChange,
		popoverType: type,
		value,
		metric,
		onActionClick,
	}: {
		chain: ChainData
		name: string
		showIcon: boolean
		actionState?: boolean
		actionText?: Nullable<string>
		popoverContent?: Nullable<string>
		change?: Nullable<number>
		inverseChange?: boolean
		popoverType?: 'info' | 'warning'
		value: Nullable<string>
		metric?: MetricDialogProps['metric']
		onActionClick?: Nullable<() => void>
	} = $props()
</script>

<Card.Root
	class={cn({ 'pointer-events-none cursor-progress opacity-50 select-none': n.l })}
	size="xs"
>
	<Card.Header>
		<Card.Title class="flex items-center gap-1 truncate">
			{#if showIcon}
				<img
					class="size-5"
					alt="{chain.name} logo"
					crossorigin="anonymous"
					decoding="async"
					fetchpriority="low"
					height="20"
					loading="lazy"
					src="https://res.cloudinary.com/dcruwhddg/image/upload/icons/{chain.slug}.svg"
					width="20"
				/>
			{/if}

			<h3 class="text-muted-foreground xs:text-sm truncate text-xs font-medium">
				{`${chain.name} ${name}`}
			</h3>
		</Card.Title>

		{#if onActionClick || metric}
			<Card.Action class="flex items-center justify-start gap-1.5">
				{#if onActionClick}
					<Tooltip.Root>
						<Tooltip.Trigger
							class="text-muted-foreground hover:text-foreground focus-visible:ring-ring flex items-center justify-center rounded-sm transition-colors focus-visible:ring-3 disabled:pointer-events-none disabled:opacity-50"
							aria-label={actionText}
							disabled={n.l}
							onclick={onActionClick}
						>
							{#if actionState}
								<ToggleRight size="16" />
							{:else}
								<ToggleLeft size="16" />
							{/if}
						</Tooltip.Trigger>

						<Tooltip.Content>
							<p>{actionText}</p>
						</Tooltip.Content>
					</Tooltip.Root>
				{/if}

				{#if metric}
					<ButtonPrimitive.Root
						class="text-muted-foreground hover:text-foreground focus-visible:ring-ring flex items-center justify-center rounded-sm transition-colors focus-visible:ring-3 disabled:pointer-events-none disabled:opacity-50"
						aria-label="Show explanation for {chain.name} {name}"
						disabled={n.l}
						onclick={() => {
							phg.capture('metric_dialog_open', { metric })

							updateMetricDialog({ open: true, metric })
						}}
						type="button"
					>
						<CircleHelp size="14" />
					</ButtonPrimitive.Root>
				{/if}
			</Card.Action>
		{/if}
	</Card.Header>

	<Card.Content class="flex flex-wrap items-center justify-start gap-1">
		<p class="xs:text-base text-sm font-medium">{value}</p>

		{#if change}
			<span
				class="flex items-center text-xs"
				class:text-green-500={inverseChange ? change < 0 : change >= 0}
				class:text-red-400={inverseChange ? change >= 0 : change < 0}
			>
				{#if change >= 0}
					<ChevronUp size="13" />
				{:else}
					<ChevronDown size="13" />
				{/if}

				{formatChange(change)}
			</span>
		{/if}

		<InfoPopover ariaLabel="More info on {chain.name} {name}" {content} {type} />
	</Card.Content>

	{#if showIcon}
		<Card.Footer class="mt-auto justify-center">
			<p class="text-center text-xs opacity-80">
				Data from
				<span class="text-branding-gradient font-bold">Chainspect</span>
			</p>
		</Card.Footer>
	{/if}
</Card.Root>
